import { Injectable, inject } from '@angular/core';
import { AlertController } from '@ionic/angular/standalone';

/** Options passed to {@link ConfirmService.confirm}. */
export interface ConfirmOptions {
  /** Dialog title. */
  title: string;
  /** Body message. */
  message: string;
  /** Label on the confirm button. @default 'Confirm' */
  confirmLabel?: string;
  /** Label on the cancel button. @default 'Cancel' */
  cancelLabel?: string;
  /** When true, the confirm button uses the `danger` color treatment. @default false */
  destructive?: boolean;
}

/**
 * Programmatic confirm dialog. Used for destructive flows like deleting a goal,
 * removing a student from a caseload, or any action that warrants explicit user
 * confirmation.
 *
 * Built on Ionic's {@link AlertController} so focus management, keyboard handling,
 * and backdrop behavior are handled by the framework. Destructive confirms use the
 * `danger` color per brand guidelines.
 *
 * @example
 * const ok = await this.confirmService.confirm({
 *   title: 'Delete goal',
 *   message: 'This action cannot be undone.',
 *   confirmLabel: 'Delete',
 *   destructive: true,
 * });
 * if (ok) this.deleteGoal();
 *
 * @feature shared
 */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private readonly alertController = inject(AlertController);

  /**
   * Present a confirm dialog and resolve with `true` when the user confirms,
   * `false` when the user cancels or dismisses.
   */
  async confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise(async resolve => {
      const alert = await this.alertController.create({
        header: options.title,
        message: options.message,
        cssClass: options.destructive ? 'confirm-destructive' : 'confirm-default',
        backdropDismiss: true,
        buttons: [
          {
            text: options.cancelLabel ?? 'Cancel',
            role: 'cancel',
            handler: () => resolve(false),
          },
          {
            text: options.confirmLabel ?? 'Confirm',
            role: 'confirm',
            cssClass: options.destructive ? 'alert-button-danger' : 'alert-button-primary',
            handler: () => resolve(true),
          },
        ],
      });

      await alert.present();
      const result = await alert.onDidDismiss();
      // Guard: if the user dismissed via backdrop, neither handler ran — resolve false.
      if (result.role !== 'confirm' && result.role !== 'cancel') {
        resolve(false);
      }
    });
  }
}
